/** Org setting from Settings → Invoicing → NEX invoice reference. */
export type NexReconciliationIdentifierMode = 'ro' | 'po' | 'both';

export interface InvoiceNexIdentifierUi {
    keyIdentifiersFieldTitle: string;
    /** Invoices list table: leading identifier column header. */
    listTableColumnTitle: string;
    /** Shown in the list when no identifier is present. */
    listTableMissingIdentifier: string;
    fieldPlaceholder: string;
    notFoundTooltipTitle: string;
    /** Second line of the not-found tooltip; pass trimmed display value. */
    notFoundTooltipValueLine: (valueTrimmed: string) => string;
    revertFieldLabel: string;
    notFoundFlagAriaLabel: string;
    flagTableDescription: string;
    flagTableNotFoundReason: string;
    reconciliationBannerNotFoundStep: string;
    reasonBadgeLabel: string;
    reasonBadgeTooltip: string;
    /** Short explanation next to the identifier badge in the invoicing user guide. */
    reasonFlagUserGuideBlurb: string;
    /** Sentence after the badge in Invoice Reason Flags help (must resolve before submit). */
    reasonFlagHelpModalMustResolveBody: string;
    /** Fragment: "a valid RO" | "a valid PO" | "a valid PO or RO" for return-slip note in flag help. */
    reasonFlagHelpModalReturnSlipValidPhrase: string;
    saveIdentifierChangeModalTitle: string;
    saveIdentifierChangeLeadIn: string;
    /** For return-slip submit gating copy (e.g. "RO #" / "PO #" / "PO or RO #"). */
    returnSlipSubmitReferencePhrase: string;
    /** Body copy under the lead-in on the save-after-identifier-change modal. */
    saveIdentifierChangeNexRefreshBody: string;
}

function normalizeMode(mode: NexReconciliationIdentifierMode | undefined): NexReconciliationIdentifierMode {
    return mode === 'po' || mode === 'both' ? mode : 'ro';
}

/** Labels and copy for invoice RO/PO/identifier UI driven by org NEX reconciliation mode. */
export function getInvoiceNexIdentifierUi(mode: NexReconciliationIdentifierMode | undefined): InvoiceNexIdentifierUi {
    const m = normalizeMode(mode);
    if (m === 'po') {
        return {
            keyIdentifiersFieldTitle: 'PO Number',
            listTableColumnTitle: 'PO / ID number',
            listTableMissingIdentifier: 'Missing PO#',
            fieldPlaceholder: 'PO Number',
            notFoundTooltipTitle: 'PO number not found in NEX',
            notFoundTooltipValueLine: (v) => `PO #${v} was not found in the NEX system`,
            revertFieldLabel: 'PO number',
            notFoundFlagAriaLabel: 'PO not found',
            flagTableDescription: 'PO Number',
            flagTableNotFoundReason: 'PO number not found in NEX',
            reconciliationBannerNotFoundStep:
                'Resolve the PO in Key Identifiers: enter a valid purchase order reference (5–7 digits) that exists in Nexsyis. If the PO is already correct, use Refresh Nexsyis data so Yaxle can load the purchase-order line pool to match against.',
            reasonBadgeLabel: 'PO',
            reasonBadgeTooltip: 'PO is missing or not found in NEX.',
            reasonFlagUserGuideBlurb: 'Purchase order reference missing or not found in Nexsyis.',
            reasonFlagHelpModalMustResolveBody:
                'The purchase order reference must be present and valid in NEX before you can submit. Fix a missing PO or a PO that is not found before using Submit.',
            reasonFlagHelpModalReturnSlipValidPhrase: 'a valid PO',
            saveIdentifierChangeModalTitle: 'Save PO change?',
            saveIdentifierChangeLeadIn: 'You changed the PO from',
            returnSlipSubmitReferencePhrase: 'PO #',
            saveIdentifierChangeNexRefreshBody:
                'Saving will re-pull purchase-order data from NEX for the updated PO and re-run reconciliation and matching.',
        };
    }
    if (m === 'both') {
        return {
            keyIdentifiersFieldTitle: 'Identifier (PO/RO) number',
            listTableColumnTitle: 'Identifier (PO/RO) number',
            listTableMissingIdentifier: 'Missing identifier',
            fieldPlaceholder: 'PO or RO number',
            notFoundTooltipTitle: 'Identifier not found in NEX',
            notFoundTooltipValueLine: (v) => `PO/RO #${v} was not found in the NEX system`,
            revertFieldLabel: 'Identifier (PO/RO) number',
            notFoundFlagAriaLabel: 'Identifier not found',
            flagTableDescription: 'Identifier (PO/RO) number',
            flagTableNotFoundReason: 'Identifier not found in NEX',
            reconciliationBannerNotFoundStep:
                'Resolve the identifier in Key Identifiers: enter a valid PO or repair order that exists in Nexsyis. Use Refresh Nexsyis data to re-run lookup (PO first, then repair order); line items match from the repair-order endpoint.',
            reasonBadgeLabel: 'PO/RO',
            reasonBadgeTooltip: 'PO/RO identifier is missing or not found in NEX.',
            reasonFlagUserGuideBlurb: 'PO or RO identifier missing or not found in Nexsyis.',
            reasonFlagHelpModalMustResolveBody:
                'The PO or RO identifier must be present and valid in NEX before you can submit. Fix a missing identifier or one that is not found before using Submit.',
            reasonFlagHelpModalReturnSlipValidPhrase: 'a valid PO or RO',
            saveIdentifierChangeModalTitle: 'Save identifier change?',
            saveIdentifierChangeLeadIn: 'You changed the identifier (PO/RO) from',
            returnSlipSubmitReferencePhrase: 'PO or RO #',
            saveIdentifierChangeNexRefreshBody:
                'Saving will re-run Nexsyis lookup (PO candidates first, then repair order) and re-match line items from the repair-order endpoint.',
        };
    }
    return {
        keyIdentifiersFieldTitle: 'RO Number',
        listTableColumnTitle: 'RO #',
        listTableMissingIdentifier: 'Missing RO#',
        fieldPlaceholder: 'RO Number',
        notFoundTooltipTitle: 'RO number not found in NEX',
        notFoundTooltipValueLine: (v) => `RO #${v} was not found in the NEX system`,
        revertFieldLabel: 'RO number',
        notFoundFlagAriaLabel: 'RO not found',
        flagTableDescription: 'RO Number',
        flagTableNotFoundReason: 'RO number not found in NEX',
        reconciliationBannerNotFoundStep:
            'Resolve the RO in Key Identifiers: use a valid repair order (5–7 digits, usually 6) that exists in Nexsyis. If the RO is already correct, use Refresh Nexsyis data so Yaxle can load the purchase-order line pool to match against.',
        reasonBadgeLabel: 'RO',
        reasonBadgeTooltip: 'RO is missing or not found in NEX.',
        reasonFlagUserGuideBlurb: 'Repair order missing or not found in Nexsyis.',
        reasonFlagHelpModalMustResolveBody:
            'The repair order must be present and valid in NEX before you can submit. Fix a missing RO or an RO that is not found before using Submit.',
        reasonFlagHelpModalReturnSlipValidPhrase: 'a valid RO',
        saveIdentifierChangeModalTitle: 'Save RO change?',
        saveIdentifierChangeLeadIn: 'You changed the RO from',
        returnSlipSubmitReferencePhrase: 'RO #',
        saveIdentifierChangeNexRefreshBody:
            'Saving will re-pull purchase-order data from NEX for the updated RO and re-run reconciliation and matching.',
    };
}

/** Read NEX invoice reference mode from the same settings document shape as invoice detail. */
export function getNexReconciliationIdentifierModeFromSettings(settings: unknown): NexReconciliationIdentifierMode {
    const root = settings as
        | {
              topSection?: { sections?: Array<{ key?: string; data?: unknown }> };
              bottomSection?: { sections?: Array<{ key?: string; data?: unknown }> };
          }
        | undefined;
    const find = (sections?: Array<{ key?: string; data?: unknown }>) =>
        sections?.find((s) => s?.key === 'invoicing') as
            | { data?: { nexReconciliationIdentifierMode?: string } }
            | undefined;
    const section = find(root?.topSection?.sections) ?? find(root?.bottomSection?.sections);
    const m = section?.data?.nexReconciliationIdentifierMode;
    return m === 'po' || m === 'both' ? m : 'ro';
}
